import type { UseFormReturn } from 'react-hook-form';

import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ContactLanguage } from '~/api/clients/types';

import { Button } from '~/components';
import { Field, Input, Select } from '~/components/Form';

import type { ContactFormType } from '../types';

import { ContactFormSection } from './ContactFormSection';
import { addButtonRowStyle, fullWidthStyle, gridStyle } from './contactFormSections.css';

type ContactPersonalInfoFormType = Pick<
  ContactFormType,
  'firstName' | 'function' | 'language' | 'lastName' | 'organisationName' | 'phoneNumber'
>;

export const ContactPersonalInfoFormSection = <T extends ContactPersonalInfoFormType>({
  form: { control, getValues },
  isPrivateClient = false,
}: {
  form: T extends ContactPersonalInfoFormType ? UseFormReturn<T> : never;
  isPrivateClient?: boolean;
}) => {
  const { t } = useTranslation(['clients', 'common', 'validation']);

  // Only a private client can be an informal association.
  const [isOrganisationNameShown, setIsOrganisationNameShown] = useState(() => !!getValues('organisationName'));

  const languageItems = (['nl', 'fr', 'en'] satisfies ContactLanguage[]).map((language) => ({
    label: t(`common:languages.${language}`),
    value: language,
  }));

  return (
    <ContactFormSection title={t('clients:createEdit.subsections.personalInfo')}>
      <div className={gridStyle}>
        {/* First name */}
        <Controller
          control={control}
          name="firstName"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('clients:fields.firstName.label')}</Field.Label>

              <Input
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('clients:fields.firstName.placeholder')}
                ref={ref}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: (value) => !!value.trim() || t('validation:required'),
          }}
        />

        {/* Last name */}
        <Controller
          control={control}
          name="lastName"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('clients:fields.lastName.label')}</Field.Label>

              <Input
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('clients:fields.lastName.placeholder')}
                ref={ref}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: (value) => !!value.trim() || t('validation:required'),
          }}
        />

        {/* Function */}
        {!isPrivateClient && (
          <Controller
            control={control}
            name="function"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label optional>{t('clients:fields.function.label')}</Field.Label>

                <Input
                  onBlur={onBlur}
                  onValueChange={onChange}
                  placeholder={t('clients:fields.function.placeholder')}
                  ref={ref}
                  value={value ?? ''}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
          />
        )}

        {/* Language */}
        <Controller
          control={control}
          name="language"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('clients:fields.lang.label')}</Field.Label>

              <Select.Root items={languageItems} onValueChange={onChange} value={value}>
                <Select.Button />

                <Select.List>
                  {languageItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: { value: true, message: t('validation:required') } }}
        />

        {/* Phone number */}
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label optional>{t('clients:fields.telephone.label')}</Field.Label>

              <Input
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('clients:fields.telephone.placeholder')}
                ref={ref}
                type="tel"
                value={value ?? ''}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
        />

        {/* Name of the informal association */}
        {isPrivateClient && (
          <div className={fullWidthStyle}>
            {isOrganisationNameShown ? (
              <Controller
                control={control}
                name="organisationName"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                  <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                    <Field.Label infotip={t('clients:fields.organisationName.tooltip')} optional>
                      {t('clients:fields.organisationName.label')}
                    </Field.Label>

                    <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value ?? ''} />

                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
              />
            ) : (
              <div className={addButtonRowStyle}>
                <Button emphasis="tertiary" icon="Add" onClick={() => setIsOrganisationNameShown(true)}>
                  {t('clients:fields.organisationName.add')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </ContactFormSection>
  );
};
